'use client'

import { ComponentProps, useMemo } from 'react'

import { OfferMessage as BaseOfferMessage } from '@marketplace-web/offers/price-offers-feature'
import { useConversationContext } from '@marketplace-web/messaging/conversation-providers-feature'
import {
  ApplicableTransactionAction,
  useNewConversation,
} from '@marketplace-web/messaging/conversation-messaging-data'
import { invariant } from '@marketplace-web/messaging/messaging-util'
import { useSession } from '@marketplace-web/shared/session-data'

import useTransactionActions from '../OfferRequestMessage/useTransactionActions'

type Props = Pick<ComponentProps<typeof BaseOfferMessage>, 'message' | 'createdTime' | 'entityType'>

const OfferMessage = (props: Props) => {
  const { conversation: oldConversation } = useConversationContext()
  const { user } = useSession()

  const conversation = useNewConversation(oldConversation!.id)

  // this weird check ensures that the `data` field is reduced to the inquiry conversation
  invariant(
    'items' in conversation.data,
    // TODO: replace with proper error class
    new Error('UnexpectedConversationTypeResponse: expected inquiry conversation type'),
  )

  const [item] = conversation.data.items
  invariant(
    item,
    // TODO: replace with proper error class
    new Error('InquiryConversationWithoutItems'),
  )

  const [oppositeUser] = conversation.oppositeUsers

  invariant(
    oppositeUser,
    // TODO: replace with proper error class
    new Error('InquiryConversationMissingOppositeUser'),
  )

  const transactionId = conversation.data.transactionId as unknown as number
  const itemId = item.inquiryItemId as unknown as number

  const transformedOppositeUser = useMemo(
    () => ({
      ...oppositeUser,
      city: null,
      localCountryTitle: '',
      isLocationExposed: null,
      login: oppositeUser.loginName,
      feedbackCount: null,
      feedbackReputation: 0,
      isModerator: false,
      isUserUnblockable: false,
      isSystem: false,
      photoThumbnail: oppositeUser.photo?.thumbnails?.[0]?.url ?? null,
      photo: null,
    }),
    [oppositeUser],
  )

  const transactionActions = useTransactionActions(itemId, conversation.data.currentUserSide)
  const buyerId = conversation.data.currentUserSide === 'buyer' ? user?.id : Number(oppositeUser.id)

  return (
    <BaseOfferMessage
      {...props}
      transactionId={transactionId}
      // TODO: fix opposite user type mismatch
      oppositeUser={transformedOppositeUser}
      isPaymentActionAvailable={transactionActions.includes(ApplicableTransactionAction.Buy)}
      // TODO: somehow resolve seller countdown
      isSellerCountdownActive={false}
      buyerId={buyerId}
    />
  )
}

export default OfferMessage
